/**
 * Notification Types
 * Frontend types for notifications
 */

export type NotificationType =
  | 'task_due_soon'
  | 'task_overdue'
  | 'task_assigned'
  | 'task_completed'
  | 'case_update'
  | 'interaction_followup'
  | 'mention'
  | 'deadline_warning'
  | 'system'
  | 'payment_received'
  | 'document_shared'
  | 'event_reminder';

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message?: string;
  actionUrl?: string;
  actionLabel?: string;
  entityType?: string;
  entityId?: string;
  entityMetadata?: Record<string, unknown>;
  read: boolean;
  readAt?: string;
  archived: boolean;
  archivedAt?: string;
  createdAt: string;
  expiresAt?: string;
}

export interface NotificationFilters {
  read?: boolean;
  archived?: boolean;
  types?: NotificationType[];
  priorities?: NotificationPriority[];
  fromDate?: string;
  toDate?: string;
}

export interface NotificationStats {
  total: number;
  unread: number;
  byType: Record<NotificationType, number>;
  byPriority: Record<NotificationPriority, number>;
}

// UI Helpers
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  task_due_soon: 'Tarefa vencendo',
  task_overdue: 'Tarefa atrasada',
  task_assigned: 'Tarefa atribuída',
  task_completed: 'Tarefa concluída',
  case_update: 'Atualização de caso',
  interaction_followup: 'Lembrete de follow-up',
  mention: 'Você foi mencionado',
  deadline_warning: 'Alerta de prazo',
  system: 'Sistema',
  payment_received: 'Pagamento recebido',
  document_shared: 'Documento compartilhado',
  event_reminder: 'Lembrete de evento'
};

export const NOTIFICATION_PRIORITY_LABELS: Record<NotificationPriority, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  normal: 'Normal',
  low: 'Baixa'
};

// Cores do tema (visual v2): positivo → primary, atraso/alerta → destructive,
// demais → neutro.
const TONE_POSITIVE = 'bg-primary/10 text-primary';
const TONE_NEGATIVE = 'bg-destructive/10 text-destructive';
const TONE_NEUTRAL = 'bg-secondary text-secondary-foreground';
const TONE_MUTED = 'bg-muted text-muted-foreground';

export const NOTIFICATION_TYPE_COLORS: Record<NotificationType, string> = {
  task_due_soon: TONE_NEGATIVE,
  task_overdue: TONE_NEGATIVE,
  task_assigned: TONE_NEUTRAL,
  task_completed: TONE_POSITIVE,
  case_update: TONE_NEUTRAL,
  interaction_followup: TONE_NEUTRAL,
  mention: TONE_NEUTRAL,
  deadline_warning: TONE_NEGATIVE,
  system: TONE_MUTED,
  payment_received: TONE_POSITIVE,
  document_shared: TONE_NEUTRAL,
  event_reminder: TONE_NEUTRAL
};

export const NOTIFICATION_PRIORITY_COLORS: Record<NotificationPriority, string> = {
  low: 'text-muted-foreground',
  normal: 'text-primary',
  high: 'text-foreground',
  urgent: 'text-destructive'
};
